import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import PostCard from "../../components/ui/PostCard";
import PageHeader from "../../components/ui/PageHeader";
import LoadingScreen from "../../components/ui/LoadingScreen";
import { GlowOrb } from "../../components/ui/Fx";
import { getPublishedPosts } from "../../utils/posts";

const CAT_COLORS = {
  "macro-note": "#818cf8", "weekly-outlook": "#f5c518",
  "trade-idea": "#4ade80", "education": "#f87171",
};

const PAGE_CONFIG = {
  insights: { label: "Macro Notes", title: "Macro Research & Insights", subtitle: "In-depth macroeconomic analysis, central bank positioning, and fundamental market commentary.", category: "macro-note", meta: "Independent macro research covering global markets, currencies, and economic fundamentals." },
  "weekly-outlook": { label: "Weekly Outlook", title: "Weekly Market Outlook", subtitle: "Structured weekly positioning — key themes, market bias, and setups to monitor across the trading week.", category: "weekly-outlook", meta: "Weekly forex and macro market outlook with structured analysis and key levels." },
  "trade-ideas": { label: "Trade Ideas", title: "Chart Analysis & Trade Ideas", subtitle: "High-probability setups identified through multi-frame analysis, with full context and reasoning.", category: "trade-idea", meta: "Forex and gold trade ideas backed by macro fundamentals and technical structure." },
  education: { label: "Education", title: "Education & Learning", subtitle: "Frameworks, concepts, and tools developed through years of real market experience.", category: "education", meta: "Trading education content focused on macro analysis, risk management, and trader psychology." },
};

export default function PostListPage({ type }) {
  const config = PAGE_CONFIG[type] || PAGE_CONFIG.insights;
  const accentColor = CAT_COLORS[config.category] || "#c9a84c";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    getPublishedPosts({ category: config.category })
      .then(setPosts).catch(console.error).finally(() => setLoading(false));
  }, [config.category]);

  const allTags = [...new Set(posts.flatMap(p => p.tags || []))];
  const filtered = posts.filter(p => {
    const matchTag = !activeTag || (p.tags || []).includes(activeTag);
    const q = search.toLowerCase();
    const matchSearch = !q || p.title.toLowerCase().includes(q) || (p.excerpt || "").toLowerCase().includes(q);
    return matchTag && matchSearch;
  });

  return (
    <>
      <Helmet>
        <title>{config.title} — Macro Trader</title>
        <meta name="description" content={config.meta} />
      </Helmet>

      <PageHeader label={config.label} title={config.title} subtitle={config.subtitle} accentColor={accentColor} />

      <div className="container" style={{ paddingBottom: 112, position: "relative" }}>
        <GlowOrb color={accentColor} size={500} top="5%" right="-5%" opacity={0.04} />
        <GlowOrb color="#c9a84c" size={400} bottom="10%" left="-5%" opacity={0.04} />

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 52, flexWrap: "wrap", alignItems: "center", padding: "20px 24px", background: "#080808", border: "1px solid #141414", borderRadius: "10px" }}>
          <input
            type="text"
            placeholder="Search posts..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="form-input"
            style={{ maxWidth: 220, padding: "9px 14px", fontSize: "13px", background: "#0a0a0a" }}
          />
          <div style={{ width: 1, height: 28, background: "#1e1e1e", flexShrink: 0 }} />
          {allTags.slice(0, 8).map(tag => (
            <button key={tag} onClick={() => setActiveTag(activeTag === tag ? "" : tag)}
              style={{
                padding: "6px 14px", fontFamily: "var(--font-mono)", fontSize: "9px",
                letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer",
                background: activeTag === tag ? accentColor + "18" : "transparent",
                color: activeTag === tag ? accentColor : "#ffffff",
                border: `1px solid ${activeTag === tag ? accentColor + "40" : "#1e1e1e"}`,
                borderRadius: "20px", transition: "all 0.2s",
                boxShadow: activeTag === tag ? `0 0 14px ${accentColor}20` : "none",
              }}
              onMouseEnter={e => { if (activeTag !== tag) { e.currentTarget.style.color = accentColor; e.currentTarget.style.borderColor = accentColor + "30"; }}}
              onMouseLeave={e => { if (activeTag !== tag) { e.currentTarget.style.color = "#ffffff"; e.currentTarget.style.borderColor = "#1e1e1e"; }}}
            >
              #{tag}
            </button>
          ))}
          {filtered.length > 0 && (
            <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: "10px", color: "#ffffff", letterSpacing: "0.1em" }}>
              {filtered.length} post{filtered.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {loading ? <LoadingScreen /> : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "100px 0" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "3rem", color: "#1e1e1e", marginBottom: 20 }}>◎</div>
            <p style={{ color: "#ffffff", fontFamily: "var(--font-mono)", fontSize: "12px", letterSpacing: "0.14em", textTransform: "uppercase" }}>No posts found</p>
          </div>
        ) : (
          <div className="grid-3">
            {filtered.map((post, i) => (
              <div key={post.id} style={{ opacity: 0, animation: `fadeUp 0.5s ${i * 0.07}s ease both` }}>
                <PostCard post={post} />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
